import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { fetchAssignees, assignSubmission } from "./api";

// Multi-select assignment dialog. onSaved(assignedTo, newStatus) fires after a
// successful save so callers can update their local copy of the submission.
const AssignDialog = ({ open, submission, onClose, onSaved }) => {
  const [assignees, setAssignees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelected((submission?.assigned_to || []).map((a) => a.id));
    fetchAssignees()
      .then(setAssignees)
      .catch(() => setError("Could not load the assignee list."));
  }, [open, submission]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const assignedTo = assignees
        .filter((a) => selected.includes(a.id))
        .map((a) => ({ id: a.id, name: a.name }));
      const result = await assignSubmission(submission._id, assignedTo);
      onSaved(assignedTo, result.status);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Assign Spec Sheet</DialogTitle>
      <DialogContent dividers>
        {error && (
          <p className="mb-3 rounded-xl bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}
        {assignees.length === 0 && !error ? (
          <p className="app-muted text-sm">
            No users hold the SBCF Team or SBCF Assigner role yet — add roles in
            User Management first.
          </p>
        ) : (
          <List dense>
            {assignees.map((a) => (
              <ListItem key={a.id} disablePadding>
                <ListItemButton onClick={() => toggle(a.id)}>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selected.includes(a.id)} />
                  </ListItemIcon>
                  <ListItemText
                    primary={a.name}
                    secondary={(a.roles || []).join(", ")}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Assignments"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDialog;
